import type {IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import {parsePinnedIssueFields} from '@github-ui/issue-fields-shared/issue-field'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {useEffect, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {graphql, type Subscription} from 'relay-runtime'

import type {
  useIssueFormSecondaryQuery,
  useIssueFormSecondaryQuery$data,
} from './__generated__/useIssueFormSecondaryQuery.graphql'

type useIssueFormSecondaryQueryProps = {
  repoId: string
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const useIssueFormSecondaryGraphqlQuery = graphql`
  query useIssueFormSecondaryQuery($id: ID!) {
    node(id: $id) {
      ... on Repository {
        securityPolicyUrl
        contributingFileUrl
        codeOfConductFileUrl
        supportFileUrl
        owner {
          ... on Organization {
            pinnedIssueFields(first: 10) {
              nodes {
                __typename
                ... on IssueFieldText {
                  id
                  name
                  dataType
                  visibility
                }
                ... on IssueFieldSingleSelect {
                  id
                  name
                  dataType
                  visibility
                }
                ... on IssueFieldDate {
                  id
                  name
                  dataType
                  visibility
                }
                ... on IssueFieldNumber {
                  id
                  name
                  dataType
                  visibility
                }
                ... on IssueFieldMultiSelect {
                  id
                  name
                  dataType
                  visibility
                }
              }
            }
            issueFields(first: 100) {
              totalCount
              nodes {
                __typename
                ... on IssueFieldText {
                  id
                  visibility
                }
                ... on IssueFieldSingleSelect {
                  id
                  visibility
                }
                ... on IssueFieldDate {
                  id
                  visibility
                }
                ... on IssueFieldNumber {
                  id
                  visibility
                }
                ... on IssueFieldMultiSelect {
                  id
                  visibility
                }
              }
            }
            issueTypes(first: 1) {
              totalCount
            }
          }
        }
      }
    }
  }
`

export type SecondaryQueryData = {
  securityPolicyUrl: string | null
  contributingFileUrl: string | null
  codeOfConductFileUrl: string | null
  supportFileUrl: string | null
  orgPinnedFields: readonly IssueFieldSelection[] | null
  fieldVisibilityMap: Record<string, string>
  orgHasIssueFields: boolean
  orgHasIssueTypes: boolean
}

export function useIssueFormSecondaryQuery({repoId}: useIssueFormSecondaryQueryProps): SecondaryQueryData | null {
  const environment = useRelayEnvironment()
  const [secondaryQueryResult, setSecondaryQueryResult] = useState<{repoId: string; data: SecondaryQueryData} | null>(
    null,
  )

  useEffect(() => {
    let rethrowTimeout: ReturnType<typeof setTimeout> | undefined
    const subscription: Subscription = clientSideRelayFetchQueryRetained<useIssueFormSecondaryQuery>({
      environment,
      query: useIssueFormSecondaryGraphqlQuery,
      variables: {
        id: repoId,
      },
    }).subscribe({
      next: (data: useIssueFormSecondaryQuery$data) => {
        const orgPinnedFields = parsePinnedIssueFields(
          data.node?.owner?.pinnedIssueFields?.nodes?.filter(node => node?.__typename !== '%other'),
        )

        const fieldVisibilityMap: Record<string, string> = {}
        for (const node of data.node?.owner?.issueFields?.nodes ?? []) {
          if (node && node.__typename !== '%other' && 'id' in node && 'visibility' in node) {
            fieldVisibilityMap[node.id] = node.visibility
          }
        }

        const orgHasIssueFields = (data.node?.owner?.issueFields?.totalCount ?? 0) > 0
        const orgHasIssueTypes = (data.node?.owner?.issueTypes?.totalCount ?? 0) > 0

        setSecondaryQueryResult({
          repoId,
          data: {
            securityPolicyUrl: data.node?.securityPolicyUrl ?? null,
            contributingFileUrl: data.node?.contributingFileUrl ?? null,
            codeOfConductFileUrl: data.node?.codeOfConductFileUrl ?? null,
            supportFileUrl: data.node?.supportFileUrl ?? null,
            orgPinnedFields,
            fieldVisibilityMap,
            orgHasIssueFields,
            orgHasIssueTypes,
          },
        })
      },
      error: (error: Error) => {
        // Rethrow asynchronously so the failure reaches the global error handler
        // (console + error reporting) instead of being swallowed by this Relay
        // subscription callback, while we still recover with safe defaults below.
        rethrowTimeout = setTimeout(() => {
          throw error
        })
        setSecondaryQueryResult({
          repoId,
          data: {
            securityPolicyUrl: null,
            contributingFileUrl: null,
            codeOfConductFileUrl: null,
            supportFileUrl: null,
            orgPinnedFields: null,
            fieldVisibilityMap: {},
            orgHasIssueFields: true,
            orgHasIssueTypes: true,
          },
        })
      },
    })

    return () => {
      subscription.unsubscribe()
      if (rethrowTimeout) clearTimeout(rethrowTimeout)
    }
  }, [environment, repoId])

  // Only return the result when it belongs to the current repository. While a new
  // request is in flight after switching repos, this prevents the previous org's
  // type/field flags, pinned fields, and visibility map from leaking into the form.
  return secondaryQueryResult?.repoId === repoId ? secondaryQueryResult.data : null
}
